import { FC } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";

import {
  GRANTVESTMENT_STATUS,
  GrantVestment,
} from "../../types/grant-vestment";
import { getPercentage, moneyFormat } from "../../utils/money-format";
import { numberOfDays, shortFormat } from "../../utils/date-format";
import imagePlaceholader from "../../assets/image-placeholader.png";
import { neutral } from "style/color";
import { TRANSACTION_STATUS } from "types/contribution-transaction";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import { useNavigate } from "react-router-dom";
import { MyGrantVestmentDetailsPage } from "constants/pages";
import { ImageDisplay } from "components/grant-vestments/Grantvestment-image";

interface FundBackedGrantVestmentCardProps {
  grantvestment: GrantVestment;
  grantVestmentRequestStatue: TRANSACTION_STATUS;
  backedAmount: number;
  approvedAmount: number | undefined;
  pendingAmount: number | undefined;
  dpi: number;
  totalDistributedAmount: number;
}

const FundBackedGrantVestmentCard: FC<FundBackedGrantVestmentCardProps> = ({
  grantvestment,
  grantVestmentRequestStatue,
  backedAmount = 0,
  approvedAmount,
  pendingAmount,
  dpi,
  totalDistributedAmount,
}) => {
  const navigate = useNavigate();
  const funded = getPercentage(
    grantvestment?.raisedAmount,
    grantvestment?.goalAmount
  );
  const daysLeft =
    numberOfDays(grantvestment?.deadline) < 0
      ? 0
      : numberOfDays(grantvestment?.deadline);

  const handleClick = () => {
    const url = MyGrantVestmentDetailsPage.replace(":id", grantvestment.name);
    navigate(url, { state: { id: grantvestment.grantVestmentId } });
  };

  const getContent = () => {
    // CLOSED Status Content
    if (grantvestment?.status === GRANTVESTMENT_STATUS.CLOSED) {
      return (
        <CardContent
          sx={{
            pt: "20px",
            px: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            minHeight: "207px",
            backgroundColor: "#FFFFFF",
            borderRadius: { xs: "0px 0px 8px 8px", md: "0px 8px 8px 0px" },
            flex: "none",
            order: 1,
            flexGrow: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
              flex: "none",
              order: 0,
              flexGrow: 0,
            }}
          >
            <Typography variant="subtitle1">{grantvestment.name}</Typography>
            <Typography
              variant="bodyCopyRegular"
              sx={{ color: "neutral.lighter" }}
            >
              {grantvestment.projectBy}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              gap: "20px",
              flex: "none",
              order: 1,
              alignSelf: "stretch",
              flexGrow: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Quattrocento Sans'",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "13px",
                    textTransform: "uppercase",
                    color: "#0B3A81",
                  }}
                >
                  {funded}% FUNDED
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    variant="smallCopyBold"
                    sx={{
                      color: neutral[500],
                      fontWeight: "700",
                    }}
                  >
                    RVPI: {moneyFormat(grantvestment?.moicValue || 0)}x
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    variant="smallCopyBold"
                    sx={{
                      color: neutral[500],
                      fontWeight: "700",
                    }}
                  >
                    DPI: {(dpi === 0) ? '0.00' : moneyFormat(dpi || 0)}x
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    variant="smallCopyBold"
                    sx={{
                      color: neutral[500],
                      fontWeight: "700",
                    }}
                  >
                    TVPI: {moneyFormat(dpi + (grantvestment?.moicValue || 0))}x
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                gap: "4px",
                minWidth: "fit-content",
                marginTop: "20px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "13px",
                  textTransform: "uppercase",
                  color: "#212121",
                  whiteSpace: "nowrap",
                }}
              >
                INVESTED
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: "20px",
                  color: "#212121",
                  whiteSpace: "nowrap",
                }}
              >
                ${moneyFormat(backedAmount!)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      );
    }

    // EXITED Status Content
    if (grantvestment?.status === GRANTVESTMENT_STATUS.EXITED) {
      return (
        <CardContent
          sx={{
            pt: "20px",
            px: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            minHeight: "207px",
            backgroundColor: "#FFFFFF",
            borderRadius: { xs: "0px 0px 8px 8px", md: "0px 8px 8px 0px" },
            flex: "none",
            order: 1,
            flexGrow: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
              flex: "none",
              order: 0,
              flexGrow: 0,
            }}
          >
            <Typography variant="subtitle1">{grantvestment.name}</Typography>
            <Typography
              variant="bodyCopyRegular"
              sx={{ color: "neutral.lighter" }}
            >
              {grantvestment.projectBy}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              width: "100%",
              gap: "20px",
              flex: "none",
              order: 1,
              alignSelf: "stretch",
              flexGrow: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'Quattrocento Sans'",
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "13px",
                    textTransform: "uppercase",
                    color: "#0B3A81",
                  }}
                >
                  EXITED
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    variant="smallCopyBold"
                    sx={{
                      color: neutral[500],
                      fontWeight: "700",
                    }}
                  >
                    Original amount invested: ${moneyFormat(backedAmount!)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Typography
                    variant="smallCopyBold"
                    sx={{
                      color: neutral[500],
                      fontWeight: "700",
                    }}
                  >
                    Total amount received: $
                    {moneyFormat(totalDistributedAmount)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                gap: "4px",
                minWidth: "fit-content",
                marginTop: "20px",
              }}
            ></Box>
          </Box>
        </CardContent>
      );
    }

    // ACTIVE Status (default) Content
    return (
      <CardContent
        sx={{
          pt: "20px",
          px: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          minHeight: "207px",
          backgroundColor: "#FFFFFF",
          borderRadius: { xs: "0px 0px 8px 8px", md: "0px 8px 8px 0px" },
          flex: "none",
          order: 1,
          flexGrow: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
            flex: "none",
            order: 0,
            flexGrow: 0,
          }}
        >
          <Typography variant="subtitle1">{grantvestment.name}</Typography>
          <Typography
            variant="bodyCopyRegular"
            sx={{ color: "neutral.lighter" }}
          >
            {grantvestment.projectBy}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            gap: "20px",
            flex: "none",
            order: 1,
            alignSelf: "stretch",
            flexGrow: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "13px",
                  textTransform: "uppercase",
                  color: "#0B3A81",
                }}
              >
                {funded}% FUNDED
              </Typography>

              {/* Progress Bar */}
              <Box
                sx={{
                  width: "100%",
                  height: "4px",
                  backgroundColor: "#E7EBF2",
                  borderRadius: "2px",
                }}
              >
                <Box
                  sx={{
                    width: `${Math.min(parseFloat(funded), 100)}%`,
                    height: "100%",
                    backgroundColor: "#0B3A81",
                    borderRadius: "2px",
                    maxWidth: "100%",
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "13px",
                  textTransform: "uppercase",
                  color: "#757575",
                  whiteSpace: "nowrap",
                }}
              >
                {daysLeft} days to go
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "13px",
                  textTransform: "uppercase",
                  color: "#757575",
                }}
              >
                -
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Quattrocento Sans'",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "13px",
                  textTransform: "uppercase",
                  color: "#757575",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                until {shortFormat(grantvestment?.deadline)}
              </Typography>
            </Box>
            {approvedAmount && approvedAmount > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  mt: 0.5,
                }}
              >
                <AccessTimeFilledIcon
                  sx={{
                    width: "12px",
                    height: "12px",
                    color: "#9E9E9E",
                  }}
                />
                <Typography
                  variant="smallCopyBold"
                  sx={{
                    color: neutral[500],
                    fontWeight: "700",
                  }}
                >
                  Pending process ${moneyFormat(approvedAmount!)}
                </Typography>
              </Box>
            ) : null}
            {pendingAmount && pendingAmount > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: neutral[500],
                  gap: "4px",
                  mt: 0.5,
                }}
              >
                <AccessTimeFilledIcon
                  sx={{
                    width: "12px",
                    height: "12px",
                    color: "#9E9E9E",
                  }}
                />
                <Typography
                  variant="smallCopyBold"
                  sx={{
                    color: neutral[500],
                    fontWeight: "700",
                  }}
                >
                  Pending acceptance ${moneyFormat(pendingAmount!)}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "flex-start",
              gap: "4px",
              minWidth: "fit-content",
              marginTop: "20px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Quattrocento Sans'",
                fontWeight: 700,
                fontSize: "12px",
                lineHeight: "13px",
                textTransform: "uppercase",
                color: "#212121",
                whiteSpace: "nowrap",
              }}
            >
              BACKED
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Quattrocento Sans'",
                fontWeight: 700,
                fontSize: "18px",
                lineHeight: "20px",
                color:
                  grantVestmentRequestStatue === TRANSACTION_STATUS.PENDING
                    ? "#9E9E9E"
                    : "#212121",
                whiteSpace: "nowrap",
              }}
            >
              ${moneyFormat(backedAmount!)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Typography
                variant="smallCopyBold"
                sx={{
                  color: neutral[500],
                  fontWeight: "700",
                }}
              >
                DPI: {(dpi === 0) ? '0.00' : moneyFormat(dpi || 0)}x
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    );
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        "&:hover": { cursor: "pointer" },
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ImageDisplay
        imageUrl={grantvestment.imageUrl}
        placeholderUrl={imagePlaceholader}
        alt={grantvestment.name}
        width={{ xs: "100%", md: "30%" }}
        aspectRatio={2}
      />

      <Box
        sx={{
          width: { xs: "100%", md: "60%" },
          display: "flex",
          flexDirection: "column",
          flex: "none",
          order: 1,
          flexGrow: 1,
        }}
      >
        {getContent()}
      </Box>
    </Card>
  );
};

export default FundBackedGrantVestmentCard;
